import { FormProvider } from 'react-hook-form';

import { useNewSankeyForm } from 'components/SankeyFormProvider';
import { useSankeyFormContext } from 'contexts/sankeyFormContext';
import { UpstreamFormContext } from 'contexts/upstreamFormContext';

interface Props {
  children: React.ReactNode;
}

/**
 * FormDraftProvider creates a draft form that can be edited independently
 * from the upstream form. It provides a way to work with temporary form
 * data that can be initialized with the upstream form's values.
 * This is useful for scenarios like editing in modals or temporary
 * views where changes should only be committed when proved valid.
 *
 * The component:
 * - Creates a new form instance with initial values from the upstream form
 * - Provides both the draft form (via FormProvider) and the upstream form
 *   (via UpstreamFormContext) to its children
 * - Does not automatically sync changes between the draft and upstream forms
 */
const FormDraftProvider: React.FC<Props> = ({ children }) => {
  const { form: upstreamForm } = useSankeyFormContext();

  // Create a new form instance using the upstream form values on component mount.
  const draftForm = useNewSankeyForm(upstreamForm.getValues());

  return (
    <UpstreamFormContext.Provider value={{ upstreamForm }}>
      <FormProvider {...draftForm}>{children}</FormProvider>
    </UpstreamFormContext.Provider>
  );
};

export default FormDraftProvider;
